import { useMemo } from "react";

import { formatPrice, getAddonsInfoByPeriod } from "app/components/PackageSwitcher/components/helpers";
import { NUMBER_OF_MONTHS } from "app/constants/NumberOfMonths";

import type {
    ISummaryPackagePrice,
    IUseSwitchPackagePriceCalculationAPI,
    IUseSwitchPackagePriceCalculationProps,
} from "./useSwitchPackagePriceCalculation.types";

export const useSwitchPackagePriceCalculation = ({
    currencySign,
    packageToChange,
    isUpgradeToAnnual,
    availableAddOns,
}: IUseSwitchPackagePriceCalculationProps): IUseSwitchPackagePriceCalculationAPI => {
    const addonsInfo = useMemo(
        () => getAddonsInfoByPeriod(availableAddOns, packageToChange.period, currencySign),
        [currencySign, availableAddOns, packageToChange.period],
    );

    const {
        initialPackagePrice,
        initialPackagePriceFormatted,
        annualDiscount = 0,
        annualDiscountFormatted = "",
        addonsPrice = 0,
        addonsPriceFormatted,
        taxFormatted,
        tax = 0,
        relativePriceWithTax,
    } = useMemo((): ISummaryPackagePrice => {
        const initialPackagePriceMonthly = packageToChange.priceOfMonthlyPackage;

        const packageTax = packageToChange?.tax || 0;
        const addonsTax = packageToChange?.addonsTax || 0;
        const totalTax = packageTax + addonsTax;
        const hasTax = totalTax !== 0;

        const relativePackageTax = packageToChange?.relativeTax || 0;
        const relativeAddonsTax = packageToChange?.addonsRelativeTax || 0;
        const relativePackagePrice = packageToChange?.relativePrice || 0;
        const relativeAddonsPrice = packageToChange?.addonsRelativePrice || 0;
        const relativeTotalPrice = relativePackageTax + relativeAddonsTax + relativePackagePrice + relativeAddonsPrice;

        if (!isUpgradeToAnnual) {
            const monthlyAddonsPrice = addonsInfo.reduce((acc, addonInfo) => acc + addonInfo.monthlyPrice, 0);

            return {
                initialPackagePrice: initialPackagePriceMonthly,
                initialPackagePriceFormatted: formatPrice(currencySign, initialPackagePriceMonthly),
                addonsPrice: monthlyAddonsPrice,
                addonsPriceFormatted: formatPrice(currencySign, monthlyAddonsPrice),
                tax: totalTax,
                taxFormatted: hasTax ? formatPrice(currencySign, totalTax) : null,
                relativePriceWithTax: relativeTotalPrice,
            };
        }

        const targetAnnualPrice = packageToChange.price;
        const initialPackagePriceAnnually = initialPackagePriceMonthly * NUMBER_OF_MONTHS;
        const priceWithAnnualDiscount = initialPackagePriceAnnually - targetAnnualPrice;

        const annualAddonsPrice = addonsInfo.reduce((acc, addonInfo) => acc + addonInfo.annuallyPrice, 0);
        return {
            initialPackagePrice: initialPackagePriceAnnually,
            initialPackagePriceFormatted: formatPrice(currencySign, initialPackagePriceAnnually),
            annualDiscount: priceWithAnnualDiscount,
            annualDiscountFormatted: formatPrice(currencySign, priceWithAnnualDiscount, true),
            addonsPrice: annualAddonsPrice,
            addonsPriceFormatted: formatPrice(currencySign, annualAddonsPrice),
            tax: totalTax,
            taxFormatted: hasTax ? formatPrice(currencySign, totalTax) : null,
            relativePriceWithTax: relativeTotalPrice,
        };
    }, [packageToChange, isUpgradeToAnnual, addonsInfo, currencySign]);

    const totalPrice = useMemo(() => {
        const finalPrice = initialPackagePrice + addonsPrice + tax - annualDiscount;
        return formatPrice(currencySign, finalPrice);
    }, [initialPackagePrice, addonsPrice, tax, annualDiscount, currencySign]);

    return {
        initialPackagePriceFormatted,
        targetPackageName: packageToChange.title,
        totalPrice,
        annualDiscountFormatted,
        addonsPriceFormatted,
        addonsInfo,
        taxFormatted,
        relativePriceWithTax,
    };
};
